import React, { useState, useEffect } from "react";
import "../Zone/Zone.css";
import "../Overview/Overview.css";
import { fetchDevicesForLocation, fetchComplaintSourceCounts } from "../../Services/locationEntries";
import ComplaintsSnapRow from "../Overview/components/ComplaintsSnapRow";
import DateRangeFilter from "../Overview/components/DateRangeFilter";
import CustomDateRange from "../Overview/components/CustomDateRange";
import { sumProceduresInRange } from "../../Utils/counts";

function SnapPill({ variant, children }) {
  return <span className={`snap-pill ${variant}`}>{children}</span>;
}

function formatTakeoverTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 86400000);
  const dayAfterStart = new Date(tomorrowStart.getTime() + 86400000);
  const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d >= todayStart && d < tomorrowStart) return `Today, ${timeStr}`;
  if (d >= tomorrowStart && d < dayAfterStart) return `Tomorrow, ${timeStr}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + `, ${timeStr}`;
}

function FeedFallback() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", gap: "8px" }}>
      <svg viewBox="0 0 24 24" width="28" height="28" style={{ stroke: "var(--t3)", fill: "none", strokeWidth: 1.5 }}>
        <path d="M23 7l-7 5 7 5V7z" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
      <span style={{ fontSize: "var(--text-base)", color: "var(--t3)" }}>No live feeds available</span>
    </div>
  );
}

function Location({ className, location, zone, ward, range, onRangeChange, onBack, onComplaintsClick, onProceduresClick }) {
  const [feeds, setFeeds] = useState(null);
  const [sourceCounts, setSourceCounts] = useState(null);

  useEffect(() => {
    if (!location?.documentId) return;
    setFeeds(null);
    fetchDevicesForLocation(location.documentId).then(setFeeds).catch(() => setFeeds([]));
  }, [location?.documentId]);

  useEffect(() => {
    if (!location?.documentId) return;
    setSourceCounts(null);
    fetchComplaintSourceCounts({ locationId: location.documentId, from: range?.from, to: range?.to })
      .then(setSourceCounts)
      .catch(() => setSourceCounts({ total: 0 }));
  }, [location?.documentId, range?.from, range?.to]);

  if (!location) return null;

  const taken = location.takeoverTime != null;
  const state = location.operationalStatus?.state || "Inactive";
  const sc = taken ? "ok" : state === "Active" ? "warn" : "bad";
  const scLabel = { ok: "Taken Over", warn: "Active", bad: "Inactive" }[sc];

  const { positive = 0, negative = 0 } = sumProceduresInRange([location], range?.from, range?.to);
  const procTotal = positive + negative;

  return (
    <div className={className} data-cy="location-screen">
      <div className="subhdr">
        <div className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </div>
        <div className="subhdr-info">
          <div className="subhdr-crumb">
            Chennai › Zone {zone?.id} › Ward {ward?.id}
          </div>
          <div className="subhdr-title">{location.location || location.PCTNo}</div>
          {!taken && location.estimatedTakeoverTime && (
            <div style={{ fontSize: "var(--text-sm)", color: "var(--t3)", marginTop: "2px" }}>
              ETA {formatTakeoverTime(location.estimatedTakeoverTime)}
            </div>
          )}
        </div>
        <span className={`pill ${sc}`}>
          <span className="dot" />
          {scLabel}
        </span>
      </div>
      <div className="divider" />
      <div className="scroll">
        <div style={{ padding: "6px 20px 10px", fontSize: "var(--text-sm)", color: "var(--t3)", fontFamily: "var(--font-mono)", letterSpacing: ".04em" }}>
          {location.PCTNo}
        </div>

        {/* Live Feeds */}
        <div className="sec-hdr">
          <div className="t-section">Live Feeds</div>
        </div>
        {feeds === null ? (
          <div style={{ height: "56px", margin: "0 20px 10px", background: "var(--surface2)", borderRadius: "6px" }} />
        ) : feeds.length === 0 ? (
          <FeedFallback />
        ) : (
          feeds.map((device, i) => (
            <div key={i} style={{ position: "relative", paddingTop: "56.25%", margin: "0 20px 10px" }}>
              <iframe
                title={`feed-${i}`}
                src={device.stream}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none", borderRadius: "6px" }}
              />
            </div>
          ))
        )}

        <div className="filter-stack" data-cy="filter-stack">
          <div className="scope-bar">
            <span className="scope-bar-title">Operations</span>
            <DateRangeFilter value={range?.type} onRangeChange={onRangeChange} />
          </div>
          {range?.type === "custom" && <CustomDateRange onRangeChange={onRangeChange} />}
        </div>

        {/* Complaints */}
        <ComplaintsSnapRow data={sourceCounts} loading={sourceCounts === null} onClick={onComplaintsClick} />

        {/* Procedures */}
        <div className="snap-row" onClick={onProceduresClick} data-cy="snap-row-procedures">
          <div className="snap-icon">
            <svg viewBox="0 0 24 24">
              <polyline points="9,11 12,14 22,4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <div className="snap-body">
            <div className="snap-name">Procedures — <strong>{procTotal}</strong> completed</div>
            <div className="snap-pills">
              <SnapPill variant="neutral">{procTotal}</SnapPill>
            </div>
          </div>
        </div>

        <div style={{ height: "20px" }} />
      </div>
    </div>
  );
}

export default Location;
